import { useMemo } from 'react';
import { isHighestPrivilegeAdmin, isStaffAccount } from '@/utils/adminRole';

interface Props {
  status: string;
  startDate: string;
  endDate: string;
  notes?: string;
  adminRole: string | null;
}

/** 狀態語義階段 */
const STATE_PHASES: Record<string, { key: string; label: string; subLabel: string; color: string; bg: string; border: string }> = {
  'pre-contract': {
    key: 'pre-contract',
    label: '契約前',
    subLabel: '尚未生效，可自由調整',
    color: 'text-slate-700',
    bg: 'bg-slate-50',
    border: 'border-slate-200',
  },
  'active-rental': {
    key: 'active-rental',
    label: '契約生效中',
    subLabel: '風險已移轉，異動受限',
    color: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
  },
  'terminal': {
    key: 'terminal',
    label: '已結案',
    subLabel: '僅供查閱，原則不可回退',
    color: 'text-gray-600',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
  },
};

/** 狀態對應階段 */
function getStatePhase(status: string): string {
  if (['draft', 'pending', 'need_info', 'approved'].includes(status)) return 'pre-contract';
  if (status === 'renting') return 'active-rental';
  if (['completed', 'cancelled', 'rejected'].includes(status)) return 'terminal';
  return 'pre-contract';
}

/** 判斷是否為逾期（不改 DB，以日期計算） */
function isOverdue(status: string, endDate: string): boolean {
  if (status !== 'renting') return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);
  return today > end;
}

/** 判斷是否為爭議 */
function isDisputed(status: string, notes?: string): boolean {
  if (status === 'need_info') return true;
  if (notes && (notes.includes('爭議') || notes.includes('糾紛') || notes.includes('dispute'))) return true;
  return false;
}

/** 判斷是否為法務保全 */
function isLegalHold(notes?: string): boolean {
  if (!notes) return false;
  const keywords = ['法律', '法務', 'legal', '訴訟', '律師', 'court', 'litigation'];
  return keywords.some((k) => notes.toLowerCase().includes(k.toLowerCase()));
}

/** 狀態是否受回退限制 */
function isRestrictedState(status: string): boolean {
  return ['renting'].includes(status);
}

/** 一般員工是否可回退 */
function canGeneralStaffRollback(status: string, role: string | null): boolean {
  if (!isRestrictedState(status)) return true;
  if (isHighestPrivilegeAdmin(role)) return true;
  if (role === 'store_manager') return true; // 店長可回退，但需 override 流程
  if (isStaffAccount(role)) return false;
  return true;
}

export default function StateGovernancePanel({ status, startDate, endDate, notes, adminRole }: Props) {
  const phase = useMemo(() => getStatePhase(status), [status]);
  const phaseInfo = STATE_PHASES[phase];

  const overdue = useMemo(() => isOverdue(status, endDate), [status, endDate]);
  const disputed = useMemo(() => isDisputed(status, notes), [status, notes]);
  const legalHold = useMemo(() => isLegalHold(notes), [notes]);
  const restricted = useMemo(() => isRestrictedState(status), [status]);
  const canRollback = useMemo(() => canGeneralStaffRollback(status, adminRole), [status, adminRole]);

  return (
    <div className="space-y-3">
      {/* 狀態語義階段標籤 */}
      <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${phaseInfo.bg} ${phaseInfo.border}`}>
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${phaseInfo.bg}`}>
          {phase === 'pre-contract' && <i className="ri-file-list-line text-xl text-slate-600"></i>}
          {phase === 'active-rental' && <i className="ri-shield-check-line text-xl text-amber-600"></i>}
          {phase === 'terminal' && <i className="ri-archive-line text-xl text-gray-500"></i>}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-semibold ${phaseInfo.color}`}>{phaseInfo.label}</span>
            <span className="text-xs text-gray-400">·</span>
            <span className="text-xs text-gray-500">{phaseInfo.subLabel}</span>
          </div>
          <div className="text-xs text-gray-400 mt-0.5">
            階段：<span className="font-medium text-gray-600">{phase === 'pre-contract' ? 'Pre-Contract' : phase === 'active-rental' ? 'Active Rental' : 'Terminal / Closed'}</span>
          </div>
        </div>
      </div>

      {/* 敏感 / 法律風險指示器 */}
      <div className="flex flex-wrap gap-2">
        {overdue && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
            <i className="ri-alarm-warning-line"></i>
            已逾期
          </span>
        )}
        {disputed && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
            <i className="ri-discuss-line"></i>
            爭議處理中
          </span>
        )}
        {legalHold && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
            <i className="ri-scales-3-line"></i>
            法務保全
          </span>
        )}
      </div>

      {/* 回退限制提示 */}
      {restricted && (
        <div className={`px-4 py-3 rounded-lg border ${canRollback ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'}`}>
          <div className="flex items-start gap-2">
            <i className={`mt-0.5 ${canRollback ? 'ri-information-line text-blue-500' : 'ri-lock-line text-red-500'}`}></i>
            <div className="text-sm">
              {canRollback ? (
                <>
                  <span className="font-medium text-blue-800">狀態回退受限</span>
                  <p className="text-blue-600 mt-0.5 text-xs">
                    當前為契約生效狀態，回退需經店長以上核准並記錄原因。
                  </p>
                </>
              ) : (
                <>
                  <span className="font-medium text-red-800">一般員工不可回退</span>
                  <p className="text-red-600 mt-0.5 text-xs">
                    此訂單已進入風險移轉階段，僅店長以上可發起回退流程。
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { isOverdue, isDisputed, isLegalHold, isRestrictedState, canGeneralStaffRollback };